/**
 * "Your confirmation also cleared 1 more Invoice": the news that learning took
 * other documents out of Needs review, said once, beside the documents list's
 * counts (owner, October 4, 2026). It used to be a card inside the NEXT
 * document's field panel, under that document's own "Needs review: …" reason,
 * in 7-pixel type, with a Put back in review button: it read as if it were about
 * the document on screen, and asked for a click to keep a lesson that had
 * already applied.
 *
 * Three paths report the same news, and some report the same documents: the
 * engine's held-work re-judge (which also records what the learner's settled
 * types released), the type recheck after a type is confirmed, and the catch-up
 * that applies the job's learning to waiting work. The news is therefore kept by
 * document, so a document counts once however many paths name it, and a document
 * the engine recorded as a release can be put back (Undo).
 *
 * The opposite news takes the same line: released documents that what the operator
 * confirmed since pulled back into review ("Your confirmation sent 2 Invoices back for
 * another look", said as just "2 Invoices back for another look"; DocumentStore.MarkPulledBack). The engine keeps those in review until
 * someone confirms them, so that line has no action, as the card it replaces had none.
 */

export type ClearedDocument = { id: string; kind: string; releaseId: string | null };

export type ClearedNews = {
  /** cleared: learning took them out of Needs review; back: it put released ones back in. */
  direction: 'cleared' | 'back';
  documents: ClearedDocument[];
  /** Grows whenever a document joins the news, so the line's time starts again. */
  serial: number;
  /** After Undo: how many went back in Needs review. */
  reopened: number | null;
  /** The kinds Undo put back, when they are known exactly. */
  reopenedKinds: string[] | null;
};

/** Keeps a count on the same line as its word: "1 Invoice", never "1" at the end of a line. */
const NBSP = ' ';
const plural = (count: number, kind: string) => `${count}${NBSP}${kind}${count === 1 ? '' : 's'}`;

/** "3 Invoices, 1 Purchase Order", most first; one kind is just "3 Invoices". */
export function clearedKinds(kinds: readonly string[]): string {
  const byKind = new Map<string, number>();
  for (const kind of kinds) byKind.set(kind, (byKind.get(kind) ?? 0) + 1);
  return [...byKind.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([kind, count]) => plural(count, kind))
    .join(', ');
}

/** Adds documents to the news (or starts it). A document already in it is not counted again. */
export function addClearedDocuments(
  previous: ClearedNews | null,
  added: readonly ClearedDocument[],
  direction: ClearedNews['direction'] = 'cleared',
): ClearedNews | null {
  // News that was put back is finished; anything cleared after that is new news.
  const base =
    previous && previous.reopened === null && previous.direction === direction ? previous : null;
  const documents = [...(base?.documents ?? [])];
  let grew = false;
  for (const document of added) {
    const at = documents.findIndex((item) => item.id === document.id);
    if (at < 0) {
      documents.push({ ...document });
      grew = true;
    } else if (!documents[at].releaseId && document.releaseId)
      documents[at] = { ...documents[at], releaseId: document.releaseId };
  }
  if (documents.length === 0) return previous;
  if (base && !grew && documents.every((item, index) => item === base.documents[index]))
    return base;
  return {
    direction,
    documents,
    serial: (previous?.serial ?? 0) + (grew ? 1 : 0),
    reopened: null,
    reopenedKinds: null,
  };
}

/** The engine releases Undo can put back. Undo is offered only when it covers every document the line names. */
export function clearedReleaseIds(news: ClearedNews): string[] {
  return [...new Set(news.documents.flatMap((item) => (item.releaseId ? [item.releaseId] : [])))];
}
export const clearedNewsUndoable = (news: ClearedNews) =>
  news.direction === 'cleared' &&
  news.reopened === null &&
  news.documents.length > 0 &&
  news.documents.every((item) => item.releaseId);

/** After Undo, with how many the engine actually put back. */
export function clearedNewsReopened(news: ClearedNews, reopened: number): ClearedNews {
  const kinds = news.documents.filter((item) => item.releaseId).map((item) => item.kind);
  return {
    ...news,
    serial: news.serial + 1,
    reopened,
    reopenedKinds: reopened === kinds.length ? kinds : null,
  };
}

/**
 * The line in parts: what it says in a list of ordinary width (`lead` + `rest`), and in a
 * narrow one (`short` + `rest`), where the whole sentence would be cut off.
 */
export function clearedNewsParts(news: ClearedNews): { lead: string; short: string; rest: string } {
  if (news.reopened !== null) {
    const what =
      news.reopenedKinds && news.reopenedKinds.length > 0
        ? clearedKinds(news.reopenedKinds)
        : plural(news.reopened, 'document');
    return { lead: '', short: '', rest: `${what} back in Needs review` };
  }
  const kinds = news.documents.map((item) => item.kind);
  const count = news.documents.length;
  if (news.direction === 'back')
    // Extremely short (owner, 2026-10-10): the reason is on each document.
    return { lead: '', short: '', rest: `${clearedKinds(kinds)} back for another look` };
  const rest =
    new Set(kinds).size <= 1
      ? `${count}${NBSP}more ${kinds[0]}${count === 1 ? '' : 's'}`
      : `${count}${NBSP}more · ${clearedKinds(kinds)}`;
  return { lead: 'Your confirmation also cleared', short: 'Cleared', rest };
}

/** The one line, in full. */
export function clearedNewsText(news: ClearedNews): string {
  const { lead, rest } = clearedNewsParts(news);
  return lead ? `${lead} ${rest}` : rest;
}
